"use client";

import { useCallback, useState } from "react";
import type { AppError, NormalizeErrorOptions } from "@/core/exceptions/exception-adapter";
import { normalizeAppError, type ErrorNormalizer } from "@/shared/lib/async-result";

export type AsyncStatus = "idle" | "loading" | "success" | "error";

export interface AsyncState<TData> {
  status: AsyncStatus;
  loading: boolean;
  error: AppError | null;
  data: TData | null;
  lastUpdatedAt: number | null;
}

type NotifyFn = (message: string, tone: "success" | "error") => void;

export interface AsyncActionDeps {
  normalizeError?: ErrorNormalizer;
}

export interface AsyncActionOptions<TData> extends NormalizeErrorOptions {
  onSuccess?: (data: TData) => void;
  onError?: (error: AppError) => void;
  mapError?: (error: unknown, normalized: AppError) => AppError | string;
  successMessage?: string;
  errorMessage?: string;
  notify?: NotifyFn;
}

function createIdleState<TData>(): AsyncState<TData> {
  return {
    status: "idle",
    loading: false,
    error: null,
    data: null,
    lastUpdatedAt: null,
  };
}

export function useAsyncAction<TData = unknown>(deps?: AsyncActionDeps) {
  const [state, setState] = useState<AsyncState<TData>>(createIdleState<TData>);

  const reset = useCallback(() => {
    setState(createIdleState<TData>());
  }, []);

  const setError = useCallback(
    (error: unknown, options?: NormalizeErrorOptions) => {
      const normalized = normalizeAppError(error, options, deps);
      setState({
        status: "error",
        loading: false,
        error: normalized,
        data: null,
        lastUpdatedAt: Date.now(),
      });
    },
    [deps],
  );

  const run = useCallback(
    async (
      fn: () => Promise<TData>,
      options?: AsyncActionOptions<TData>,
    ): Promise<TData | undefined> => {
      setState((current) => ({
        ...current,
        status: "loading",
        loading: true,
        error: null,
      }));

      try {
        const data = await fn();
        const lastUpdatedAt = Date.now();

        setState({
          status: "success",
          loading: false,
          error: null,
          data,
          lastUpdatedAt,
        });

        if (options?.successMessage) {
          options.notify?.(options.successMessage, "success");
        }

        options?.onSuccess?.(data);
        return data;
      } catch (caught) {
        const normalized = normalizeAppError(caught, options, deps);
        const mapped = options?.mapError?.(caught, normalized);
        const error =
          typeof mapped === "string"
            ? { ...normalized, message: mapped }
            : (mapped ?? {
                ...normalized,
                message: options?.errorMessage ?? normalized.message,
              });

        setState({
          status: "error",
          loading: false,
          error,
          data: null,
          lastUpdatedAt: Date.now(),
        });

        options?.notify?.(error.message, "error");
        options?.onError?.(error);
        return undefined;
      }
    },
    [deps],
  );

  return {
    state,
    run,
    reset,
    setError,
  };
}
